import { AnyComponentData, ColumnMap, Component, ConnectionSchemaField, Edge, Field } from '../package.models';
import { getComponentId, getComponentType, getDataFromComponent } from './components.helpers';
import { COMPONENT_TYPE } from '../../constants/component_type';

function generateField(field: Field & ConnectionSchemaField): Field & ConnectionSchemaField {
  return Object.keys(field)
    .filter((key) => !['id', 'category'].includes(key))
    .reduce((acc, key) => ({ ...acc, [key]: field[key] }), {} as Field);
}

export function modifyComponentBeforeSave(
  component: AnyComponentData,
  componentType: keyof Component,
  isSaveAction = false,
): AnyComponentData {
  const newComponent: AnyComponentData = {
    ...component,
    connection: {
      id: component.connection?.id,
      name: component.connection?.name,
      type: component.connection?.type,
    },
    alias: component.name,
  };

  if (newComponent.schema) {
    newComponent.schema = {
      ...component.schema,
    };

    if (newComponent.schema.fields) {
      newComponent.schema.fields = (newComponent.schema.fields || []).map(generateField);
    }
  }

  if (newComponent.column_mappings) {
    newComponent.column_mappings = newComponent.column_mappings.map((item) => {
      const field: ColumnMap = {
        ...item,
      };

      if (item.objectField) {
        const key =
          item.objectField.relationKeys.find((keyItem) => keyItem.name === item.relationship_field_name) || {};
        field.relationship_name = item.objectField.relationship_name;
        field.relationship_field_type = key.type;
        field.is_reference_key = key.is_reference_key;
        field.relationship_field_name = item.relationship_field_name;
      }

      return {
        column_name: field.column_name,
        field_name: field.field_name,
        field_type: field.field_type,
        is_reference_key: field.is_reference_key,
        relationship_field_name: field.relationship_field_name ? field.relationship_field_name : undefined,
        relationship_field_type: field.relationship_field_type ? field.relationship_field_type : undefined,
        relationship_name: field.relationship_name ? field.relationship_name : undefined,
        is_merge_key: field.is_merge_key ? field.is_merge_key : undefined,
        is_bson: field.is_bson ? field.is_bson : undefined,
      } as ColumnMap;
    });
  }

  ['fields', 'partitioned_fields', 'ordered_fields', 'windowed_fields', 'grouped_fields', 'aggregated_fields'].forEach(
    (attributeName) => {
      if (newComponent[attributeName]) {
        newComponent[attributeName] = newComponent[attributeName].map(generateField);
      }
    },
  );

  if (componentType === COMPONENT_TYPE.ADWORDS_SOURCE_COMPONENT) {
    newComponent.schema.fields = newComponent.schema.fields.filter((field) => field.name !== 'customer_id');
  }

  if (componentType === COMPONENT_TYPE.CLOUD_STORAGE_SOURCE_COMPONENT) {
    if (isSaveAction) {
      if (newComponent.before_action === 'copy_manifest') {
        if (newComponent.manifest_path) {
          newComponent.before_action = 'copy';
        } else {
          newComponent.before_action = 'none';
          newComponent.manifest_connection = null;
        }
      } else if (newComponent.before_action === 'copy') {
        delete newComponent.manifest_connection;
        delete newComponent.manifest_path;
      }
    }

    if (isSaveAction) {
      const pathField = newComponent.schema.fields.find((field) => field.name === 'file_path');

      if (pathField) {
        newComponent.source_path_field_alias = pathField.alias;
        // when saving actual taken fields (for no apparent reason) are from _selectedFields
        newComponent.schema.fields = newComponent.schema.fields.filter((field) => field.name !== 'file_path');
      } else {
        newComponent.source_path_field_alias = '';
      }
    }
  }

  return newComponent;
}

export function updateComponentInPackageComponents(components: Component[], component: AnyComponentData): Component[] {
  return components.map((item, index) => {
    if (getComponentId(item) === component.id) {
      const componentType = getComponentType(components[index]);
      let newComponent = modifyComponentBeforeSave(component, componentType, true);
      newComponent = {
        ...getDataFromComponent(item),
        ...newComponent,
      };

      if (componentType === COMPONENT_TYPE.SALESFORCE_DESTINATION_COMPONENT) {
        if (!newComponent.track_errors) {
          delete newComponent.track_errors;
          delete newComponent.errors_output_connection;
          delete newComponent.errors_output_path;
        }
      }

      return {
        [componentType]: newComponent,
      } as any;
    }
    return item;
  });
}

export function updateComponentsInPackageComponents(
  components: Component[],
  componentsToUpdate: AnyComponentData[],
): Component[] {
  return components.map((item, index) => {
    const component = componentsToUpdate.find((com) => com.id === getComponentId(item));
    if (component) {
      return {
        [getComponentType(components[index])]: {
          ...getDataFromComponent(item),
          ...component,
        },
      } as any;
    }
    return item;
  });
}

export function updateComponentsOrder(edges: Edge[], components: Component[]): Edge[] {
  const newEdges = [...edges];
  components.forEach((component, index) => {
    const foundEdgeIndex = edges.findIndex((edge) => edge.source === getDataFromComponent(component).id);

    newEdges[foundEdgeIndex] = { ...edges[foundEdgeIndex], order: index };
  });

  return [...newEdges];
}
